"use client";

import { useEffect } from "react";

function animateCount(el: HTMLElement) {
  const raw = el.textContent?.trim() ?? "";
  const match = raw.match(/^([^0-9]*)([0-9]+(?:\.[0-9]+)?)([^0-9]*)$/);
  if (!match) return;
  const [, pre, numStr, suf] = match;
  const target = parseFloat(numStr);
  if (!target) return;
  const dur = Math.min(1200 + target * 0.4, 1600);
  const t0 = performance.now();
  const step = (now: number) => {
    const p = Math.min((now - t0) / dur, 1);
    const eased = 1 - (1 - p) ** 3;
    el.textContent = pre + Math.round(eased * target) + suf;
    if (p < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

export function PortfolioEffects() {
  useEffect(() => {
    // ---- mobile nav ----
    const navToggle = document.getElementById("navToggle");
    const mobileNav = document.getElementById("mobileNav");
    if (!navToggle || !mobileNav) return;

    const closeMobile = () => {
      navToggle.classList.remove("open");
      mobileNav.classList.remove("open");
      navToggle.setAttribute("aria-expanded", "false");
      document.body.style.overflow = "";
    };
    const onToggle = () => {
      const open = navToggle.classList.toggle("open");
      mobileNav.classList.toggle("open", open);
      navToggle.setAttribute("aria-expanded", String(open));
      document.body.style.overflow = open ? "hidden" : "";
    };
    navToggle.addEventListener("click", onToggle);
    mobileNav.querySelectorAll("a").forEach((a) =>
      a.addEventListener("click", closeMobile),
    );

    // ---- active nav link on scroll ----
    const onScroll = () => {
      const sections = ["about", "work", "timeline"];
      let active = "";
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < 200) active = id;
      }
      document.querySelectorAll(".nav .links a").forEach((a) =>
        a.classList.toggle("active", a.getAttribute("href") === `#${active}`),
      );
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    // ---- smooth anchor clicks ----
    const onAnchorClick = (e: Event) => {
      const a = e.currentTarget as HTMLAnchorElement;
      const id = a.getAttribute("href");
      if (!id || id.length <= 1) return;
      const target = document.querySelector(id);
      if (target) {
        e.preventDefault();
        window.scrollTo({
          top: (target as HTMLElement).offsetTop - 80,
          behavior: "smooth",
        });
      }
    };
    document.querySelectorAll('a[href^="#"]').forEach((a) =>
      a.addEventListener("click", onAnchorClick),
    );

    // ---- reveal on scroll (with optional data-i stagger) ----
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const el = e.target as HTMLElement;
          const idx =
            el.dataset.i !== undefined ? parseInt(el.dataset.i, 10) : -1;
          if (idx >= 0) {
            const delay = idx * 70;
            el.style.transitionDelay = `${delay}ms`;
            el.addEventListener(
              "transitionend",
              () => {
                el.style.transitionDelay = "";
              },
              { once: true },
            );
          }
          el.classList.add("in");
          io.unobserve(el);
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" },
    );
    document.querySelectorAll(".reveal").forEach((el) => io.observe(el));

    // ---- stat counter ----
    const countIO = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          animateCount(e.target as HTMLElement);
          countIO.unobserve(e.target);
        }
      },
      { threshold: 0.6 },
    );
    document.querySelectorAll(".stat .n").forEach((el) => countIO.observe(el));

    // ---- id-card 3d tilt ----
    const MAX_TILT = 15;
    const SCALE = 1.04;
    const cards = document.querySelectorAll<HTMLElement>(".id-card");

    const onMove = (e: MouseEvent) => {
      const card = e.currentTarget as HTMLElement;
      const rect = card.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      const rotX = -y * MAX_TILT;
      const rotY = x * MAX_TILT;
      card.style.transition = "transform .1s cubic-bezier(.03,.98,.52,.99), box-shadow .1s";
      card.style.transform = `perspective(800px) rotateX(${rotX}deg) rotateY(${rotY}deg) scale3d(${SCALE},${SCALE},${SCALE})`;
    };

    const onLeave = (e: MouseEvent) => {
      const card = e.currentTarget as HTMLElement;
      card.style.transition = "transform .6s cubic-bezier(.03,.98,.52,.99), box-shadow .6s";
      card.style.transform = "perspective(800px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)";
    };

    cards.forEach((card) => {
      card.addEventListener("mousemove", onMove);
      card.addEventListener("mouseleave", onLeave);
    });

    return () => {
      cards.forEach((card) => {
        card.removeEventListener("mousemove", onMove);
        card.removeEventListener("mouseleave", onLeave);
      });

      navToggle.removeEventListener("click", onToggle);
      mobileNav
        .querySelectorAll("a")
        .forEach((a) => a.removeEventListener("click", closeMobile));
      window.removeEventListener("scroll", onScroll);
      document
        .querySelectorAll('a[href^="#"]')
        .forEach((a) => a.removeEventListener("click", onAnchorClick));
      io.disconnect();
      countIO.disconnect();
    };
  }, []);

  return null;
}
